import React, { useEffect, useState } from "react";
import { EventsListModule } from "../../carlsberg-headless/types/modules/EventsList";
import { IterableModule } from "../../carlsberg-headless/types/modules/util/module";
import { getReactCMSComponent } from "../../carlsberg-headless/getReactCMSComponent";
import { FlexRow, FlexCell } from "@shared-components/FlexGrid/FlexGrid";
import { Container } from "@shared-components/Container/Container";
import { layoutProps, ListLayout } from "@styles/utils/getLayout";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { EventContentElement } from "../../carlsberg-headless/types/content-elements/Event";
import { Nullable } from "../../shared/types/util";
import { useApi } from "../../carlsberg-headless/api/useApi";

const spacing = [2, 2, 3];

export interface EventListProps extends EventsListModule {
  todayText?: string;
  priceText?: string;
}
interface EventRepeatProps {
  taxonomi: layoutProps[];
  events: Nullable<EventContentElement[]>;
  cbKey: string;
  todayText?: string;
  priceText?: string;
}
const EventRepeater = ({
  taxonomi,
  events,
  cbKey,
  todayText,
  priceText,
}: EventRepeatProps) => {
  let rowIndex = 0 as number;
  const rows = [];

  let cellIndex = 0 as number;
  let cells = [];

  let eventIndex = 0 as number;

  while (events && events.length) {
    const row = taxonomi[rowIndex];
    const cell = row.children && row.children[cellIndex];
    const event = events.shift();
    if (!event) {
      break;
    }

    if (todayText) event["todayText"] = todayText;
    if (priceText) event["priceText"] = priceText;

    if (cell) {
      event["ratio"] = cell.ratio;
      cells.push(
        <FlexCell
          key={`children_cell_${cbKey}_${rowIndex}_${cellIndex}_${eventIndex}`}
          col={cell.col}
        >
          {getReactCMSComponent({
            alias: event.alias,
            props: { ...event },
          })}
        </FlexCell>
      );

      cellIndex++;

      if ((row && !row.children[cellIndex]) || events.length === 0) {
        rows.push(
          <FlexCell
            key={`cell_${cbKey}_${rowIndex}_${eventIndex}`}
            col={row.col}
          >
            <FlexRow spacing={spacing}>{cells}</FlexRow>
          </FlexCell>
        );

        cells = [];
        cellIndex = 0;
        rowIndex++;
      }
    } else {
      event["ratio"] = row.ratio;
      rows.push(
        <FlexCell key={`cell_${cbKey}_${rowIndex}_${eventIndex}`} col={row.col}>
          {getReactCMSComponent({
            alias: event.alias,
            props: { ...event },
          })}
        </FlexCell>
      );

      rowIndex++;
    }

    if (!taxonomi[rowIndex]) {
      rowIndex = 0;
    }

    eventIndex++;
  }

  return rows;
};

const PureEventList: React.FC<EventListProps> = ({
  taxonomies,
  events,
  cbKey,
  todayText,
  priceText,
  id,
}) => {
  const cbSDK = useApi();
  const { currentCulture } = useSiteContext();

  const [remoteEvents, setRemoteEvents] = useState<EventContentElement[]>([]);
  const [remoteEventsLoading, setRemoveEventsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function loadRemoteEvents() {
      if (!id) {
        return;
      }

      const { content: data } = await cbSDK.getPredefinedEventList(
        id as string,
        currentCulture,
        undefined,
        undefined,
        undefined,
        false
      );

      setRemoveEventsLoading(false);

      setRemoteEvents(data as unknown as EventContentElement[]);
    }

    loadRemoteEvents();
  }, [id, cbSDK, currentCulture]);

  const eventsToRender =
    id && remoteEventsLoading
      ? []
      : id && !remoteEventsLoading
      ? remoteEvents
      : events;

  const listLayout = new ListLayout(taxonomies, "hc_list_layout_1");
  if (!listLayout.taxonomi) {
    return (
      <Container key={cbKey}>
        {(eventsToRender as unknown as IterableModule[]).map(
          ({ alias, ...props }) => {
            if (todayText) props["todayText"] = todayText;
            if (priceText) props["priceText"] = priceText;
            return getReactCMSComponent({
              alias,
              props,
            });
          }
        )}
      </Container>
    );
  } else {
    return (
      <Container
        key={cbKey}
        sx={{ px: eventsToRender?.length === 1 ? [25, 0] : [] }}
      >
        <FlexRow
          spacing={spacing}
          sx={
            eventsToRender?.length === 1
              ? {
                  justifyContent: "center",
                }
              : {}
          }
        >
          {EventRepeater({
            taxonomi: listLayout.taxonomi,
            events: [...(eventsToRender as EventContentElement[])],
            cbKey,
            todayText,
            priceText,
          })}
        </FlexRow>
      </Container>
    );
  }
};

const EventList: React.FC<EventsListModule> = (props) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  return (
    <PureEventList
      todayText={get(
        DICTIONARY_MAP.eventcard_module.eventcard_today_text,
        currentCulture
      )}
      priceText={get(
        DICTIONARY_MAP.eventcard_module.eventcard_price_text,
        currentCulture
      )}
      {...props}
    />
  );
};

export { EventList, PureEventList };
